import { useCallback, useEffect, useState } from "react";
import { Crown, Globe, RefreshCw } from "lucide-react";
import { toast } from "react-toastify";

import { useTranslation } from "../../i18n";
import { apiFetch } from "../../api/apiFetch";
import {
    FLOW_SHORT_KEY,
    TROPHY_DEPARTMENTS,
    daysSince,
    formatPoints,
    type TrophyFlow,
} from "../gamification/trophies";
import "../../styles/admin.css";
import "../../styles/logs.css";
import "../../styles/points-admin.css";

export interface WorldRecordRow {
    flow: string;
    windowMinutes: number;
    uid: number;
    name: string | null;
    warehouse: string | null;
    items: number;
    points: number;
    setAt: string;
    prevName: string | null;
    prevItems: number | null;
}

/**
 * Warehouse > Points > Records.
 *
 * Every worldwide crown, grouped by sub-department. Records rank on ITEMS
 * PROCESSED in the rolling window — points are context — so the item count
 * leads each row. The previous holder is shown too: the takeover is the
 * story a records board exists to tell.
 */
const PointsRecordsAdmin = () => {
    const { t } = useTranslation();
    const [records, setRecords] = useState<WorldRecordRow[]>([]);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await apiFetch(`/admin/points/records`, {});
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const data = await res.json();
            setRecords(Array.isArray(data?.records) ? data.records : []);
        } catch {
            setRecords([]);
            toast.error(t("server_error"), { toastId: "points-records-err" });
        } finally {
            setLoading(false);
        }
    }, [t]);

    useEffect(() => { void load(); }, [load]);

    const windowLabel = (w: number) =>
        w < 60 ? `${w} ${t("trophies_min")}` : `${w / 60} ${t("trophies_hr")}`;
    const fmtDate = (iso: string) => {
        try {
            return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
        } catch {
            return "—";
        }
    };

    // Parent departments group their child flows; departments and flows with
    // no crowns yet simply don't appear.
    const departments = TROPHY_DEPARTMENTS
        .map(dept => ({
            ...dept,
            flows: dept.flows.filter(f => records.some(r => r.flow === f)),
        }))
        .filter(dept => dept.flows.length > 0);

    return (
        <div className="logs-admin users-admin">
            <div className="users-admin-filters">
                <button
                    type="button"
                    className="logs-action-btn"
                    onClick={() => void load()}
                    aria-label={t("refresh")}
                    title={t("refresh")}
                >
                    <RefreshCw size={18} />
                </button>
            </div>

            {loading ? (
                <div className="locations-table-empty">{t("loading")}</div>
            ) : records.length === 0 ? (
                <div className="locations-table-empty">{t("points_records_empty")}</div>
            ) : (
                departments.map(dept => (
                    <div key={dept.key}>
                        <div className="points-records-flow">
                            <Globe size={15} color="#f59e0b" />
                            {t(`trophies_dept_${dept.key}`)}
                        </div>
                        {dept.flows.map(flow => (
                            <div key={flow}>
                                {dept.flows.length > 1 && (
                                    <div className="points-records-subflow">
                                        {t(FLOW_SHORT_KEY[flow as TrophyFlow])}
                                    </div>
                                )}
                                <div className="locations-table-wrap">
                            <table className="locations-table points-records-table">
                                <thead>
                                    <tr>
                                        <th>{t("points_col_window")}</th>
                                        <th>{t("trophies_items")}</th>
                                        <th>{t("score_label")}</th>
                                        <th>{t("points_col_holder")}</th>
                                        <th>{t("logs_col_warehouse")}</th>
                                        <th>{t("points_col_set")}</th>
                                        <th>{t("points_col_held")}</th>
                                        <th>{t("trophies_previous")}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {records.filter(r => r.flow === flow).map(r => (
                                        <tr key={`${r.flow}-${r.windowMinutes}`}>
                                            <td>{windowLabel(r.windowMinutes)}</td>
                                            <td>
                                                <span className="points-score-value">
                                                    {formatPoints(r.items)}
                                                </span>
                                            </td>
                                            <td>{formatPoints(r.points)}</td>
                                            <td>
                                                <Crown size={12} className="points-rank-icon points-rank-1" />
                                                {" "}{r.name ?? `#${r.uid}`}
                                            </td>
                                            <td>{r.warehouse ?? "—"}</td>
                                            <td>{fmtDate(r.setAt)}</td>
                                            <td>{daysSince(r.setAt)}{t("trophies_days_short")}</td>
                                            <td>
                                                {r.prevName
                                                    ? `${r.prevName}${r.prevItems != null ? ` (${formatPoints(r.prevItems)})` : ""}`
                                                    : "—"}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                                </div>
                            </div>
                        ))}
                    </div>
                ))
            )}
        </div>
    );
};

export default PointsRecordsAdmin;
